import type { ReactNode } from 'react';
import { useRef, useState } from 'react';

import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import { Box, ButtonBase, TableCell, TableRow, useTheme } from '@mui/material';
import { usePostHog } from 'posthog-js/react';

import CustomCheckbox from '@/components/Checkbox';
import DateText from '@/components/DateText';
import { DateTextVariants } from '@/components/DateText/useDateText';
import IndeterminateCheckbox from '@/components/IndeterminateCheckbox';
import { Permission } from '@/entities/permissions';
import useSelectionFunctions from '@/features/SymbolSelector/hooks/useSelectionFunctions';
import RestrictedIconWithTooltip from '@/pages/Markets/MarketsComponents/CommoditiesTable/CommoditiesTableTab/CommoditiesTableRow/RestrictedIconWithTooltip';
import usePermission from '@/shared/hooks/usePermission';
import { formatNumberWithPrecision } from '@/utils/formatNumberWithPrecision';
import { getDiffColor } from '@/utils/getDiffColor';

import useCommoditiesSelection from '../../useCommoditiesSelection';
import CollapsedRow from './CollapsedRow';

import type { CommoditiesTableRowType, CommoditiesTableSectionType } from '../../../types';

const CommoditiesTableRow = ({
  row,
  sections,
  index,
  shouldRenderDiff = true,
  shouldRenderDiffChange = true,
  shouldRenderLast = true,
  shouldRenderLastChange = true
}: {
  row: CommoditiesTableRowType;
  sections: CommoditiesTableSectionType[];
  index: number;
  shouldRenderDiff?: boolean;
  shouldRenderDiffChange?: boolean;
  shouldRenderLast?: boolean;
  shouldRenderLastChange?: boolean;
}) => {
  const theme = useTheme();
  const {
    shortTitle,
    date,
    last,
    delta,
    diff,
    diffDelta,
    symbol: { key },
    diffSymbol: { key: diffKey }
  } = row;
  const lastSymbol = key;
  const diffSymbol = diffKey;
  const { hasPermission } = usePermission();
  const isRestricted = !hasPermission(Permission.ViewMarketScreens);
  const restrictedData = (!last && !diff) || isRestricted;
  const rowRef = useRef<HTMLTableRowElement | null>(null);

  const returnBackgroundColor = () => {
    if (restrictedData) return theme.palette.table.infoRowBg;
    if (index % 2 === 1) return 'secondary.main';
    return 'inherit';
  };
  const [open, setOpen] = useState(false);
  const { isSymbolSelected, toggleSymbolInSelection, isSpotLocked } = useSelectionFunctions();
  const { toggleCodeInSelection } = useCommoditiesSelection(sections);
  const posthog = usePostHog();

  const trackTableRowClick = (clickedRow: CommoditiesTableRowType) => {
    posthog.capture('table_row_click', {
      symbol: clickedRow.symbol,
      diffSymbol: clickedRow.diffSymbol
    });
  };

  const renderRestrictedOrValue = (value: ReactNode, restricted: boolean | undefined) =>
    restricted ? <RestrictedIconWithTooltip /> : value;

  return (
    <>
      <TableRow
        key={shortTitle}
        ref={rowRef}
        className='markets-table-row'
        sx={{
          '&:last-child td, &:last-child th': { pb: 1 },
          backgroundColor: returnBackgroundColor(),
          '&:first-of-type td, &:first-of-type th': { pt: 1 }
        }}
        onClick={() => {
          trackTableRowClick(row);
        }}
      >
        <TableCell
          sx={cellTheme => ({
            p: 0,
            pl: 1,
            pr: 1,
            borderLeft: open ? `2px solid ${cellTheme.palette.primary.main}` : 'none'
          })}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ButtonBase
              aria-label='expand row'
              disabled={restrictedData}
              style={{ paddingRight: 5 }}
              onClick={() => {
                setOpen(!open);
              }}
            >
              <KeyboardArrowUp
                sx={{
                  fontSize: 14,
                  transform: open ? 'rotate(0deg)' : 'rotate(180deg)',
                  transition: 'transform cubic-bezier(0.5, 0, 0.5, 1) 0.1s'
                }}
              />
            </ButtonBase>
            {lastSymbol.Code && diffSymbol.Code ? (
              <IndeterminateCheckbox
                disabled={restrictedData}
                firstSelected={isSymbolSelected(lastSymbol)}
                secondSelected={isSymbolSelected(diffSymbol)}
                modeLocked={isSpotLocked}
                onLockedClick={() => {
                  toggleCodeInSelection(
                    row,
                    isSymbolSelected(lastSymbol) && isSymbolSelected(diffSymbol)
                  );
                }}
                onChange={() => {
                  toggleCodeInSelection(
                    row,
                    isSymbolSelected(lastSymbol) && isSymbolSelected(diffSymbol)
                  );
                }}
              />
            ) : null}
            {lastSymbol.Code && !diffSymbol.Code ? (
              <CustomCheckbox
                checked={isSymbolSelected(lastSymbol)}
                disabled={restrictedData}
                modeLocked={isSpotLocked}
                onLockedClick={() => {
                  toggleSymbolInSelection(lastSymbol);
                }}
                onChange={() => {
                  toggleSymbolInSelection(lastSymbol);
                }}
              />
            ) : null}{' '}
            {!lastSymbol.Code && diffSymbol.Code ? (
              <CustomCheckbox
                checked={isSymbolSelected(diffSymbol)}
                disabled={restrictedData}
                modeLocked={isSpotLocked}
                onLockedClick={() => {
                  toggleSymbolInSelection(diffSymbol);
                }}
                onChange={() => {
                  toggleSymbolInSelection(diffSymbol);
                }}
              />
            ) : null}
          </Box>
        </TableCell>
        <TableCell
          component='th'
          scope='row'
          sx={{
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {shortTitle}
        </TableCell>
        <TableCell align='right'>
          <DateText
            dateString={date}
            variant={DateTextVariants.DAY_MONTH}
          />
        </TableCell>
        {shouldRenderLast && (
          <TableCell align='right'>
            {renderRestrictedOrValue(last ? formatNumberWithPrecision(last) : '', restrictedData)}
          </TableCell>
        )}
        {shouldRenderLastChange && (
          <TableCell
            align='right'
            sx={{ color: getDiffColor(Number(delta)) }}
          >
            {renderRestrictedOrValue(delta ? formatNumberWithPrecision(delta) : '', isRestricted)}
          </TableCell>
        )}
        {shouldRenderDiff && (
          <TableCell align='right'>
            {renderRestrictedOrValue(diff ? formatNumberWithPrecision(diff) : '', restrictedData)}
          </TableCell>
        )}
        {shouldRenderDiffChange && (
          <TableCell
            align='right'
            sx={{ color: isRestricted ? '' : getDiffColor(Number(diffDelta)) }}
          >
            {renderRestrictedOrValue(
              diffDelta ? formatNumberWithPrecision(diffDelta) : '',
              restrictedData
            )}
          </TableCell>
        )}
      </TableRow>
      <TableRow
        sx={{
          backgroundColor: returnBackgroundColor()
        }}
      >
        <CollapsedRow
          diffSymbol={diffSymbol}
          lastSymbol={lastSymbol}
          open={open}
          row={row}
        />
      </TableRow>
    </>
  );
};

export default CommoditiesTableRow;
